import { Injectable } from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";
import { Store } from "@ngrx/store";
import { AlertComponent } from "@obg/mat.ui.core";
import {
	AbstractTranslatableAlertService,
	AccountStatus,
	AuthSelector,
	AuthenticationStatus,
	CoreState,
	CustomerComplianceService,
	SessionSelector,
} from "@obg/ngx.core";
import { isLocalStorageSupported } from "@obg/ngx.global";
import { EMPTY, Observable, combineLatest } from "rxjs";
import { delay, distinctUntilChanged, filter, pairwise, switchMap, take } from "rxjs/operators";

import { LoginUiConfigService } from "../login.config";

import { LoginFortyDaysReviewDialogComponent } from "./login-forty-days-review-dialog.component";
import { LOGIN_FORTY_DAYS_REVIEW_ALERT_ID } from "./login-forty-days-review-dialog.const";

@Injectable({
	providedIn: "root",
})
export class LoginFortyDaysReviewDialogService {
	private readonly INVALID_ACCOUNT_STATUSES = [AccountStatus.TemporaryAwaitingVerification, AccountStatus.Temporary];
	private readonly REVIEW_REQUIRED = this.loginUiConfigService.getLoginFortyDaysReview();

	private afterLogin$ = combineLatest([
		this.store.select(this.sessionSelector.isAuthenticatedThroughLogin()),
		this.store.select(AuthSelector.isAuthenticated),
	]).pipe(
		pairwise(),
		distinctUntilChanged(),
		filter(([[authThroughLoginPrev, authPrev], [authThroughLoginNext, authNext]]) => {
			if (isLocalStorageSupported()) {
				const shouldDialogBeOpen = localStorage.getItem(LOGIN_FORTY_DAYS_REVIEW_ALERT_ID);
				if (shouldDialogBeOpen === "1" && !authPrev && authNext) {
					return true;
				}
			}
			return !authThroughLoginPrev && authThroughLoginNext;
		})
	);

	constructor(
		private store: Store<CoreState>,
		private translatableAlertService: AbstractTranslatableAlertService,
		private customerComplianceService: CustomerComplianceService,
		private loginUiConfigService: LoginUiConfigService,
		private sessionSelector: SessionSelector
	) {}

	initialize$() {
		return this.afterLogin$.pipe(
			filter(() => this.REVIEW_REQUIRED),
			switchMap(() =>
				this.customerComplianceService.getCurrentCompliance$().pipe(
					take(1),
					filter((compliance) => {
						const isAuthenticated = compliance.authenticationStatus === AuthenticationStatus.Authenticated;
						return (
							isAuthenticated &&
							Boolean(compliance.accountInformation?.accountExpiry) &&
							Boolean(compliance.accountInformation?.accountStatus)
						);
					}),
					// Delay is needed to push the opening of the dialog after the mobile login back navigation is done.
					delay(0),
					switchMap((compliance) => {
						const accountExpiry = compliance.accountInformation?.accountExpiry;
						const accountStatus = compliance.accountInformation?.accountStatus as AccountStatus;
						const activeDaysUntilExpiration = this.calculateAccountActiveDays(accountExpiry);
						const isAccountActive = activeDaysUntilExpiration > 0;
						const isAccountStatusValid = !this.INVALID_ACCOUNT_STATUSES.includes(accountStatus);

						if (isAccountActive || isAccountStatusValid) {
							return EMPTY;
						}
						return this.openDialog$().pipe(take(1));
					})
				)
			)
		);
	}

	private openDialog$(): Observable<MatDialogRef<AlertComponent>> {
		return this.translatableAlertService.open$(
			{
				id: LOGIN_FORTY_DAYS_REVIEW_ALERT_ID,
				testId: LOGIN_FORTY_DAYS_REVIEW_ALERT_ID,
				messageComponent: LoginFortyDaysReviewDialogComponent,
				mustBeInteracted: true,
				hideClose: true,
				dialogConfig: {
					disableClose: true,
				},
			},
			[]
		);
	}

	private calculateAccountActiveDays(dateString?: string): number {
		if (!dateString) {
			return 0;
		}

		if (dateString.slice(0, 10).split("-")[2].length === 4) {
			dateString = `${dateString.slice(0, 10).split("-").reverse().join("-")}T${dateString.split("T")[1]}`;
		}

		const date = new Date(dateString);
		const difference = date.getTime() - new Date().getTime();

		return Math.ceil(difference / (1000 * 3600 * 24));
	}
}
